/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

// libraries
import React, { useEffect, createRef } from 'react';
import { useUIDSeed } from 'react-uid';
import {
    Card, Grid, makeStyles, Typography, useMediaQuery,
} from '@material-ui/core';
import {
    arrayOf, shape, string, number, bool,
} from 'prop-types';
import Slider from 'react-slick';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import { useSelector } from 'react-redux';
import ReactHTMLParser from 'react-html-parser';
import { Link } from 'react-router-dom';
import { getPresentationFamily, getPriceRangeLayout } from '../../../../../../../../state/ducks/App/ducks/Config/Config-Selectors';

// UI Components
import customBreakpoints from '../../../../../../../helpers/customBreakpoints';
import EventTracker from '../../../../../../../helpers/Qubit/QubitTracker';
import { getSSRDeviceType } from '../../../../../../../../state/ducks/App/App-Selectors';
import ProductImage from '../../CommonComponent/ProductImage';
import ProductName from '../../CommonComponent/ProductName';
import ProductPrice from '../../CommonComponent/ProductPrice';
import ProductTitle from '../../../../Partials/ProductTitle';

const useStyles = makeStyles((theme) => ({
    title: (styles) => ({
        color: styles?.displayTitleFontColor || '#000000',
        fontFamily: styles?.displayTitleFontFamily || theme?.typography?.fontFamily,
        fontSize: styles?.displayTitleFontSize || '1.625rem',
        fontStyle: styles?.displayTitleFontStyle || 'normal',
        // add other header tags here ONLY if needed for SEO/styling
        '& h3, & p': {
            margin: styles?.displayTitleMargin || '44px 0 20px 0',
            textAlign: 'center',
            fontSize: styles?.displayTitleFontSize || '1.625rem',
            fontWeight: '400',
            letterSpacing: '.4px',
            color: 'inherit',
            [theme.breakpoints.down(customBreakpoints.tabPortrait)]: {
                margin: styles?.displayTitleMargin || '30px 0 20px 0',
                fontSize: '1rem',
                fontWeight: '500',
            },
        },
    }),
    productContainerWithScroll: {
        display: 'flex',
        margin: '0 auto',
        padding: '0 0 10px 0',
        width: '100%',
        maxWidth: '1400px',
        justifyContent: 'flex-start',
        overflowX: 'auto',
        [theme.breakpoints.down(768)]: {
            padding: '0px 15px',
        },
    },
    productContainer: {
        display: 'flex',
        margin: '0 auto',
        padding: '0 0 10px 0',
        width: '100%',
        maxWidth: '1400px',
        justifyContent: 'center',
        '&--space-evenly': {
            justifyContent: 'space-around',
            [theme.breakpoints.down(1025)]: {
                justifyContent: 'space-around',
                overflowX: 'inherit',
            },
        },
        '&--center': {
            justifyContent: 'center',
            [theme.breakpoints.down(1025)]: {
                justifyContent: 'center',
            },
        },
        [theme.breakpoints.down(1025)]: {
            overflowX: 'scroll',
            justifyContent: 'flex-start',
        },
    },
    main: {
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
        '& .slick-track': {
            display: 'flex',
            justifyContent: 'center',
        },
        '& .slick-slide': {
            display: 'inline-block',
            padding: '0 10px',
            position: 'relative',
        // [theme.breakpoints.down(770)]: {
            //     margin: '0 10px',
        // },
        },
    },
    container: {
        '& .slick-track': {
            width: '100% !important',
        },
        // '& .slick-slide': {
        //     maxWidth: 'calc(18.5%)',
        // },
        // [theme.breakpoints.down(770)]: {
        //     '& .slick-slide': {
        //         maxWidth: 'calc(33.3%) !important',
        //     },
        // },
    },
    sliderContainer: (props) => ({
        '& .slick-list': {
            overflow: 'inherit',
            margin: '0 0px',
            [theme.breakpoints.down(768)]: {
                marginBottom: 30,
            },
        },
        '& .slick-prev, & .slick-next': {
            transform: 'translate(0,-50%)',
            cursor: 'pointer',
            border: 'none',
            outline: 0,
            fontSize: 0,
            lineHeight: 0,
            top: '42%',
            width: '16px',
            height: '30px',
            position: 'absolute',
            background: 'transparent',
            fill: '#000',
            zIndex: 9,
        },
        '& .slick-prev': {
            left: 0,
        },
        '& .slick-next': {
            right: 0,
        },
        '& .slick-dots': {
            listStyle: 'none',
            display: 'flex !important',
            padding: '0',
            justifyContent: 'center',
            margin: '0',
            bottom: 0,
            '& li': {
                lineHeight: '1',
                '& button': {
                    '&::before': {
                        width: theme.spacing(3),
                        height: theme.spacing(3),
                        border: `1px solid ${theme.palette.common.black}`,
                        content: '""',
                        fontSize: '1.25rem',
                        borderRadius: '50%',
                        backgroundColor: theme.palette.black,
                    },
                },
            },
            '& .slick-active': {
                '& div': {
                    background: theme.palette.cms?.sliderNavDotSelectedBackground || 'rgb(47, 47, 47)',
                },
            },
            '& .slick-current': {
                '& div': {
                    paddingRight: '5px',
                },
            },
        },
        [theme.breakpoints.between(602, 770)]: {
            '& .slick-track': {
                width: props.totalNumberOfProducts <= 3 && '100% !important',
            },
            '& .slick-slide': {
                maxWidth: props.totalNumberOfProducts <= 3 && 'calc(33.3%) !important',
            },
        },

        [theme.breakpoints.up(1000)]: {
            padding: '0 70px',
            '& .slick-prev, & .slick-next': {
                width: '25px',
            },
        },
        [theme.breakpoints.up(600)]: {
            padding: '0 15px',
            '& .slick-prev, & .slick-next': {
                width: '25px',
            },
        },
        [theme.breakpoints.down('xs')]: {
            padding: '0px',
            '& .slick-prev, & .slick-next': {
                width: '25px',
            },
        },
    }),
    slideItem: {
        height: '100%', // full height based on parent
        '& > div.rl-box': {
            position: 'inherit',
        },
        // [theme.breakpoints.down(768)]: {
        //     padding: '0 10px',
        // },
    },
    gridContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-evenly',
    },
    productLink: {
        width: '100%',
        display: 'inline-block',
        textDecoration: 'none',
        color: theme.palette.cms?.black || '#000',
        '& .MuiCard-root': {
            overflow: 'visible',
        },
        // [theme.breakpoints.down(768)]: {
        //     width: '160px',
        // },
    },
    imageCard: (styles) => ({
        width: '100%',
        paddingBottom: '100%', // DO NOT CHANGE pre-renders height according to img aspect ratio - only change if cdn height/width change
        marginBottom: styles?.isSubcollectionBanner ? '0px' : '8px',
        borderRadius: 0,
        position: 'relative',
        background: styles?.isSubcollectionBanner ? 'none !important' : '#e3e3e3',
        boxShadow: styles?.isSubcollectionBanner ? 'none' : '0 2px 4px 0 rgba(0,0,0,0.2)',
    }),
    productContentWrapper: (styles) => ({
        padding: styles?.isSubcollectionBanner ? '0px 10px' : '',
        '& p': {
            lineHeight: '1.3',
            fontSize: '0.875rem',
            [theme.breakpoints.up(769)]: {
                lineHeight: '1.4',
                fontSize: '1rem',
            },
            [theme.breakpoints.down(768)]: {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: '2',
                WebkitBoxOrient: 'vertical',
            },
        },
    }),
    productContentWrapperPlants: {
        display: 'flex',
        '& div': {
            '& p': {
                lineHeight: '1.3',
                fontSize: '1rem',
            },
            '& $sellerName': {
                fontSize: '0.813rem',
                margin: '0',
                [theme.breakpoints.down(768)]: {
                    fontSize: '0.75rem',
                },
            },
        },
        [theme.breakpoints.down(768)]: {
            display: 'block',
        },
    },
    productNameWrapper: {
        width: '60%',
        [theme.breakpoints.down(768)]: {
            width: '100%',
        },
    },
    productPriceWrapper: {
        width: '40%',
        textAlign: 'right',
        [theme.breakpoints.down(768)]: {
            width: '100%',
            textAlign: 'left',
        },
    },
    productListingPrice: {
        color: theme.palette.product?.retailPrice || theme.palette.colorPrimary,
        fontWeight: '700',
        fontSize: '1rem',
    },
    productPrice: {
        fontWeight: 'bold',
    },
}));

// Smoothing out scroll behavior
let firstClientX;
let clientX;

// eslint-disable-next-line consistent-return
const preventTouch = (e) => {
    const minValue = 5; // threshold

    clientX = e.touches[0].clientX - firstClientX;

    // Vertical scrolling does not work when you start swiping horizontally.
    if (Math.abs(clientX) > minValue) {
        e.preventDefault();
        e.returnValue = false;

        return false;
    }
};

const touchStart = (e) => {
    firstClientX = e.touches[0].clientX;
};

const ProductCarouselBuilder = ({
    blockData,
    blockData: {
        wcs_style,
        product_to_show,
    },
    products,
    nDisplayProducts,
    eventTrackingData,
    demandType,
    qubitImpressionFired, // used to avoid duplicate tracking coming from RE v Q flow
    title,
}) => {
    // Smooth scroll behavior by managing vertical v horizontal scrolling when customer interacts with recs container
    const containerRef = createRef();
    useEffect(() => {
        if (containerRef.current) {
            containerRef.current.addEventListener('touchstart', touchStart);
            containerRef.current.addEventListener('touchmove', preventTouch, {
                passive: false,
            });
        }

        return () => {
            if (containerRef.current) {
                containerRef.current.removeEventListener('touchstart', touchStart);
                containerRef.current.removeEventListener('touchmove', preventTouch, {
                    passive: false,
                });
            }
        };
    });

    const seed = useUIDSeed();
    const deviceType = useSelector(getSSRDeviceType);
    const presentationFamily = useSelector(getPresentationFamily);
    const priceRangeLayout = useSelector(getPriceRangeLayout);
    const isFoodBrand = presentationFamily === 'food';

    let displayTitle = blockData.title;
    const displayTitleFontColor = blockData.title_font_color;
    const displayTitleFontSize = blockData.title_font_size;
    const displayTitleFontStyle = blockData.title_font_style;

    if (displayTitle === '') displayTitle = title;

    const styles = {
        displayTitleMargin: blockData.title_margin,
        displayTitleFontColor,
        displayTitleFontSize,
        displayTitleFontStyle,
    };
    styles.totalNumberOfProducts = products.length;
    const classes = useStyles(styles);

    // Qubit Event Tracking
    if (eventTrackingData.callbackData && !qubitImpressionFired) {
        useEffect(() => {
            EventTracker.trackEvent({ callbackData: eventTrackingData.callbackData, eventType: 'impression' });
            const partNumbers = products.map((a) => a.partNumber);
            if (partNumbers.length > 0) {
                EventTracker.trackEvent({ callbackData: eventTrackingData.callbackData, eventType: 'impression', partNumber: partNumbers });
            }
        }, [eventTrackingData.callbackData]);
    }

    const isMobile = deviceType === 'mobile';
    const isMobileScreen = !useMediaQuery('(max-width: 767px)');
    const shouldScrollContainer = products.length > nDisplayProducts || (wcs_style && products.length > nDisplayProducts / 2);
    const productLength = products.length?.length || products.length;
    const { callbackData: callbackDataClickFromRec } = eventTrackingData;

    /**
     * Reconcile carousel styling for wcs_style flag
     *
     * wcs_style flag makes the iamges significantly larger meanings many adjustments need
     * to be made depending on how many images are in the carousel
     */
    let productUIContainerClass = classes.productContainer;
    // the images arent that big and there arent too many of them so center the images
    // in the carousel
    if (!shouldScrollContainer && !wcs_style && products?.length < 3) {
        productUIContainerClass = `${productUIContainerClass} ${productUIContainerClass}--center`;
    }

    // if the images are large and we arent going to scroll, space them evenly for wcs
    if (!shouldScrollContainer && wcs_style) {
        productUIContainerClass = `${productUIContainerClass} ${productUIContainerClass}--space-evenly`;
    }

    // if we are going to scroll, its going to flex start
    if (shouldScrollContainer) {
        productUIContainerClass = classes.productContainerWithScroll;
    }

    let productsToShow;
    if (products) {
        const maxToShow = product_to_show?.desktop || 4;
        productsToShow = products.length >= maxToShow ? maxToShow : productLength;
    }
    if (!products || productsToShow < 1) {
        return null;
    }
    if (products && productsToShow > 0) {
        return (
            <div>
                <div aria-hidden="true">
                    <ProductTitle title={displayTitle} />
                </div>
                <div ref={containerRef} className={`${productUIContainerClass} ${classes.gridContainer}`}>
                    <Slider
                        dots={false}
                        speed={800}
                        slidesToShow={productsToShow}
                        slidesToScroll={productsToShow}
                        swipe
                        draggable={false}
                        infinite={products.length >= productsToShow}
                        nextArrow={<ArrowForwardIosIcon />}
                        prevArrow={<ArrowBackIosIcon />}
                        className={`
                        ${classes.main}
                        ${products.length <= 3 && !isMobileScreen && classes.container}
                        ${(products.length > 3) && classes.sliderContainer }
                    `}
                        responsive={[
                            {
                                breakpoint: 600,
                                settings: {
                                    slidesToShow: product_to_show?.tablet || 2,
                                    slidesToScroll: product_to_show?.tablet || 2,
                                    infinite: products.length >= 2,
                                    draggable: true,
                                    dots: true,
                                    arrows: false,
                                },
                            },
                        ]}
                    >
                        {products?.map((product) => {
                            const {
                                name, image, seo: { url }, partNumber, skuPriceRange,
                            } = product;
                            let link = url;
                            const isRange = skuPriceRange?.sale?.[0]?.value !== skuPriceRange?.sale?.[1]?.value;
                            if (demandType && url) {
                                link = link.indexOf('?') >= 0 ? `${link}&demandType=${demandType}` : `${link}?demandType=${demandType}`;
                            }
                            return (
                                <div className={classes.slideItem} key={seed(link || name || partNumber)}>
                                    <Link
                                        data-test="personalized-product"
                                        title={`${ReactHTMLParser(name)}`}
                                        to={`/p${link}`}
                                        className={classes.productLink}
                                        onClick={() => {
                                            if (callbackDataClickFromRec) {
                                                EventTracker.trackEvent({ eventType: 'clickthrough', callbackData: callbackDataClickFromRec });
                                                EventTracker.trackEvent({ eventType: 'clickthrough', callbackData: callbackDataClickFromRec, partNumber });
                                            }
                                        }}
                                    >
                                        <Card className={classes.imageCard}>
                                            <ProductImage image={image} name={name} />
                                        </Card>
                                        <Grid className={classes.productContentWrapperPlants}>
                                            <Grid item className={classes.productNameWrapper}>
                                                <ProductName isFoodBrand={isFoodBrand} name={name} />
                                            </Grid>
                                            <Grid item className={classes.productPriceWrapper}>
                                                <Typography className={classes.productPrice}>
                                                    <ProductPrice
                                                        presentationFamily={presentationFamily}
                                                        priceRangeLayout={priceRangeLayout}
                                                        skuPriceRange={skuPriceRange}
                                                        isMobile={isMobile}
                                                        isRange={isRange}
                                                    />
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Link>
                                </div>
                            );
                        })}
                    </Slider>
                </div>
            </div>
        );
    }

    return null;
};

ProductCarouselBuilder.propTypes = {
    products: arrayOf(shape({
        id: string.isRequired,
        image: shape({
            path: string.isRequired,
            name: string.isRequired,
        }).isRequired,
        name: string.isRequired,
        skuPriceRange: shape({
            retail: arrayOf(shape({
                value: number.isRequired,
            })).isRequired,
            sale: arrayOf(shape({
                value: number.isRequired,
            })).isRequired,
        }).isRequired,
        seo: shape({
            url: string.isRequired,
        }).isRequired,
    })).isRequired,
    nDisplayProducts: number.isRequired, // break point between scrolling/non-scrolling

    /**
     * This is variables coming from the homepage and SSR.
     * To make this component work elsewhere, it is no longer
     * marked as required but is required for anything on the homepage.
     *
     * Currently, that includes:
     * - Trending Products Carousel
     * - QubitSkeletonRecommendations
     * - ProductRecommendationBuilder
     *
     * For other components, use title and titleFontColor props.
     */
    blockData: shape({
        title: string,
        title_margin: string,
        title_font_color: string,
        title_font_size: string,
        title_font_style: string,
        // title_font_family is the value of the Font extension value from CMS, needs to be parsed
        title_font_family: string,
    }),
    title: string,
    demandType: string,
    eventTrackingData: shape({
        callbackData: string,
        productName: string,
    }),
    qubitImpressionFired: bool,
};

ProductCarouselBuilder.defaultProps = {
    blockData: {
        title: '',
        title_margin: '',
        title_font_color: '',
        title_font_size: '',
        title_font_style: '',
        title_font_family: '',
    },
    // 4810; title can be empty, removed default 'Other Products'
    title: '',
    demandType: '',
    eventTrackingData: {
        callbackData: '',
        productName: '',
    },
    qubitImpressionFired: false,
};

export default ProductCarouselBuilder;
